import { autoinject, computedFrom, transient } from "aurelia-framework";
import { AssignRuleCollectionsToRuleRequest, RuleCollectionClientModel } from "../../../../../../models/rules/rules.models";
import { VettingType } from "../../../../../../models/vetting/vetting.models";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { NotificationClientService, NotificationType } from "../../../../../../services/basics/notification-client.service";
import { RulesClientService } from "../../../../../../services/rules/rules-client.service";

@autoinject
@transient()
export class AssignCollectionsLinkedToRuleDialog 
{
    getVettingType() {
       return this.isScreeningCollectionType? VettingType.AutoScreening: this.isDueDiligenceCollectionType? VettingType.DueDiligence: VettingType.FullVet;
    }

    private _search: string;

    rule: { ruleConfigId: number, ruleCode: string, ruleName: string};
    onSave: () => void

    ruleCollections: RuleCollectionClientModel[] = [];
    assignedRuleCollections: RuleCollectionClientModel[] = [];    
    filteredRuleCollections: RuleCollectionClientModel[] = [];

    assigningRuleCollections: { ruleCollectionId: number, isCritical: boolean }[] = [];
    unassigningRuleCollections: number[] = [];

    isScreeningCollectionType: boolean = false;
    isDueDiligenceCollectionType: boolean = false;
    isCritical: boolean = true;

    set search(x: string) {
        this._search = x;
        this.filterRuleCollections();
    }

    @computedFrom("_search")
    get search() {
        return this._search;
    }

    @computedFrom("assigningRuleCollections", "assigningRuleCollections.length",
        "ruleCollections", "ruleCollections.length",
        "assignedRuleCollections", "assignedRuleCollections.length",
        "unassigningRuleCollections", "unassigningRuleCollections.length",
        "_search", "isScreeningCollectionType", "isDueDiligenceCollectionType")
    get allAssignedRuleCollections()
    {
        const assigingIds = this.assigningRuleCollections.map(x => x.ruleCollectionId);
        const assigingRuleCollections = this.ruleCollections.filter(x => assigingIds.indexOf(x.ruleCollectionId) > -1);
        let _allAssignedRuleCollections = this.assignedRuleCollections.filter(x => this.unassigningRuleCollections.indexOf(x.ruleCollectionId) == -1)
            .concat(assigingRuleCollections);
        
        if(this._search)
        {
            _allAssignedRuleCollections = _allAssignedRuleCollections.filter(x => (x.displayName && x.displayName.toLowerCase().indexOf(this.search.toLowerCase()) > -1) 
                || (x.name && x.name.toLowerCase().indexOf(this.search.toLowerCase()) > -1));
        }

        const vettingTypeId = this.getVettingType();
        _allAssignedRuleCollections = _allAssignedRuleCollections.filter(x => x.vettingTypeId == vettingTypeId);

        return _allAssignedRuleCollections;
    }

    constructor(private dialogClientService: DialogClientService, private rulesClientService: RulesClientService,
        private notificationClientService: NotificationClientService)
    {

    }

    async activate(model: { rule: { ruleConfigId: number, ruleCode: string, ruleName: string}, onSave: () => void})
    {
        this.rule = model.rule;
        this.onSave = model.onSave;
        
        this.ruleCollections = await this.rulesClientService.getRuleCollections(false);
        this.assignedRuleCollections = await this.rulesClientService.getRuleCollectionsForRule(this.rule.ruleConfigId);

        this.filterRuleCollections();
    }

    close(){
        this.dialogClientService.hide();
    }

    filterRuleCollections() {
        if (this.search) {
            this.filteredRuleCollections = this.ruleCollections.filter(x => (x.displayName && x.displayName.toLowerCase().indexOf(this.search.toLowerCase()) > -1) || (x.name && x.name.toLowerCase().indexOf(this.search.toLowerCase()) > -1));
        }
        else {
            this.filteredRuleCollections = this.ruleCollections;
        }

        const vettingTypeId = this.getVettingType();

        const selectedRuleCollectionIds = this.allAssignedRuleCollections.map(x => x.ruleCollectionId);
        this.filteredRuleCollections = this.filteredRuleCollections.filter(x => selectedRuleCollectionIds.indexOf(x.ruleCollectionId) == -1)
            .filter(x => x.vettingTypeId == vettingTypeId);        
    }    

    assign(ruleCollection: RuleCollectionClientModel)
    {
        if(!this.assigningRuleCollections)
            this.assigningRuleCollections = [];
        
        if(this.assignedRuleCollections.map(x => x.ruleCollectionId).indexOf(ruleCollection.ruleCollectionId) == -1)
            this.assigningRuleCollections.push({ ruleCollectionId: ruleCollection.ruleCollectionId, isCritical: this.isScreeningCollectionType? this.isCritical: false });

        const unassigningIndex = this.unassigningRuleCollections.indexOf(ruleCollection.ruleCollectionId);
        if(unassigningIndex > -1)
            this.unassigningRuleCollections.splice(unassigningIndex, 1);

        this.filterRuleCollections();
    }

    unassign(ruleCollection: RuleCollectionClientModel)
    {
        if(!this.assigningRuleCollections)
            this.assigningRuleCollections = [];
        
        if(this.assignedRuleCollections.map(x => x.ruleCollectionId).indexOf(ruleCollection.ruleCollectionId) > -1)
            this.unassigningRuleCollections.push(ruleCollection.ruleCollectionId);
        else
        {
            const assigningIndex = this.assigningRuleCollections.findIndex(x => x.ruleCollectionId == ruleCollection.ruleCollectionId);
            if(assigningIndex > -1)
                this.assigningRuleCollections.splice(assigningIndex, 1);
        }

        this.filterRuleCollections();
    }

    
    unassignAll() {
        
        this.assigningRuleCollections = [];
        
        this.assignedRuleCollections.forEach(x => {
            this.unassigningRuleCollections.push(x.ruleCollectionId);
        });

        this.filterRuleCollections();
    }

    assignAll() {
        for (const filteredRuleCollection of this.filteredRuleCollections) {
            const existing = this.assignedRuleCollections.filter(x => x.ruleCollectionId == filteredRuleCollection.ruleCollectionId)[0];
            if (!existing) {
                this.assigningRuleCollections.push({ ruleCollectionId: filteredRuleCollection.ruleCollectionId, isCritical: this.isScreeningCollectionType? this.isCritical: false });
            }
                                    
            const unassigningIndex = this.unassigningRuleCollections.indexOf(filteredRuleCollection.ruleCollectionId);
            if(unassigningIndex > -1)
                this.unassigningRuleCollections.splice(unassigningIndex, 1);
        }

        this.filterRuleCollections();
    }

    save()
    {
        return new Promise<void>((resolve, reject) => {
            const request: AssignRuleCollectionsToRuleRequest = {
                ruleId: this.rule.ruleConfigId,
                assignedRuleCollections: this.assigningRuleCollections,
                unassignedRuleCollectionIds: this.unassigningRuleCollections
            };

            this.rulesClientService.assignRuleCollectionsToRule(request).
            then(() => {
                this.notificationClientService.show("Success!", "Rule assigned Successfully!", NotificationType.Success);
                if(this.onSave)
                    this.onSave();
                this.close();
                resolve();
            })
            .catch(e => {
                this.notificationClientService.show("Failed!", "Failed to assign rule", NotificationType.Error);
                reject(e);
            });
        });
    }

    ruleCollectionTypeChanged()
    {
        this.filterRuleCollections();
    }
}
